import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Droplet, Gauge, Settings as SettingsIcon, Hourglass } from 'lucide-react';
import DryerIcon from './DryerIcon';
import { getProductIcon } from './ProductIcon';
import GrainBinIcon from './GrainBinIcon';

// Mode numbers as the panel streams them.
const LOCAL = 10;
const MANUAL = 11;
const AUTOMATIC = 12;

export const MODE_COLOR = { [LOCAL]: '#2196f3', [MANUAL]: '#ff9800', [AUTOMATIC]: '#4caf50' };
export const MODE_NAME = { [LOCAL]: 'Local', [MANUAL]: 'Manual', [AUTOMATIC]: 'Automatic' };

const MOISTURE_COLOR = '#2196f3';
const RATE_COLOR = '#ff9800';

// Keypad entry, the way the panel and the phone take a setpoint. The value is
// prefilled from the live reading on open only — refreshing it on every stream
// tick would overwrite whatever is being typed.
const SetpointDialog = ({ open, title, subtitle, current, unit, color, min, max, onCancel, onSubmit }) => {
  const [entry, setEntry] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setEntry(current !== null && current !== undefined ? String(Number(current)) : '');
      setError(null);
      setBusy(false);
    }
  }, [open, current]);

  if (!open) return null;

  const press = (key) => {
    setError(null);
    if (key === 'clear') setEntry('');
    else if (key === 'back') setEntry((prev) => prev.slice(0, -1));
    else if (key === '.') setEntry((prev) => (prev.includes('.') ? prev : prev + '.'));
    else setEntry((prev) => prev + key);
  };

  const submit = async () => {
    if (busy) return;
    if (entry === '') {
      setError('Enter a value first.');
      return;
    }
    // Anything past two decimals rounds, the same as the panel keypad.
    const value = Math.round(parseFloat(entry) * 100) / 100;
    if (Number.isNaN(value) || value < min || value > max) {
      setError(`Enter a value between ${min} and ${max}.`);
      return;
    }
    setBusy(true);
    try {
      await onSubmit(value);
    } catch (err) {
      setError(err?.message || 'The dryer did not accept that change.');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onCancel}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 flex items-center justify-between" style={{ background: color }}>
          <div>
            <h3 className="text-lg font-bold text-white">{title}</h3>
            {subtitle && <p className="text-sm text-white/80">{subtitle}</p>}
          </div>
          <button onClick={onCancel} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
            <XMarkIcon className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="p-5">
          <div className="text-center mb-4">
            <span className="text-5xl font-bold text-gray-800">{entry || '0'}</span>
            {unit && <span className="text-2xl text-gray-400 ml-1">{unit}</span>}
          </div>

          {error && <p className="text-sm text-red-600 text-center mb-3">{error}</p>}

          <div className="grid grid-cols-3 gap-2">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'back'].map((key) => (
              <button
                key={key}
                onClick={() => press(key)}
                className="py-3 rounded-lg bg-gray-100 hover:bg-gray-200 text-xl font-semibold text-gray-700 transition-colors"
              >
                {key === 'back' ? '⌫' : key}
              </button>
            ))}
          </div>

          <button
            onClick={() => press('clear')}
            className="w-full mt-2 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-semibold text-gray-600 transition-colors"
          >
            Clear
          </button>

          <div className="flex gap-3 mt-4">
            <button
              onClick={onCancel}
              disabled={busy}
              className="flex-1 py-3 rounded-lg bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={busy}
              className="flex-1 py-3 rounded-lg text-white font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ background: color }}
            >
              {busy ? 'Applying…' : 'Apply'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Pick a mode, then confirm. Selecting alone changes nothing, which is the
// difference from the old admin behaviour where a click applied immediately.
const ModeDialog = ({ open, currentMode, autoLocked, autoLockedReason, onCancel, onConfirm }) => {
  const [selected, setSelected] = useState(currentMode);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setSelected(currentMode);
      setError(null);
      setBusy(false);
    }
  }, [open, currentMode]);

  if (!open) return null;

  const modes = [
    { value: LOCAL, label: 'Local', description: 'Rate set at dryer panel' },
    { value: MANUAL, label: 'Manual', description: 'Set rate manually' },
    {
      value: AUTOMATIC,
      label: 'Automatic',
      description: autoLocked ? autoLockedReason : 'Dryer Master sets the rate'
    }
  ];

  const confirm = async () => {
    if (busy || selected === currentMode) return;
    setBusy(true);
    try {
      await onConfirm(selected);
    } catch (err) {
      setError(err?.message || 'The dryer did not accept that change.');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onCancel}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 flex items-center justify-between bg-gray-800">
          <h3 className="text-lg font-bold text-white">Control Mode</h3>
          <button onClick={onCancel} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
            <XMarkIcon className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="p-5 space-y-2">
          {modes.map((mode) => {
            const locked = mode.value === AUTOMATIC && autoLocked;
            const active = selected === mode.value;
            return (
              <button
                key={mode.value}
                onClick={() => !locked && setSelected(mode.value)}
                disabled={locked}
                className={`w-full p-3 rounded-lg border-2 text-left transition-colors ${
                  locked ? 'opacity-50 cursor-not-allowed border-gray-200' : 'cursor-pointer'
                }`}
                style={
                  active && !locked
                    ? { borderColor: MODE_COLOR[mode.value], backgroundColor: `${MODE_COLOR[mode.value]}14` }
                    : { borderColor: '#e5e7eb' }
                }
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold" style={{ color: locked ? '#9ca3af' : MODE_COLOR[mode.value] }}>
                      {mode.label}
                    </p>
                    <p className="text-xs text-gray-500">{mode.description}</p>
                  </div>
                  {mode.value === currentMode && (
                    <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">Current</span>
                  )}
                </div>
              </button>
            );
          })}

          {error && <p className="text-sm text-red-600 text-center pt-1">{error}</p>}

          <div className="flex gap-3 pt-2">
            <button
              onClick={onCancel}
              disabled={busy}
              className="flex-1 py-3 rounded-lg bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={confirm}
              disabled={busy || selected === currentMode}
              className="flex-1 py-3 rounded-lg bg-gray-800 text-white font-semibold hover:bg-gray-900 transition-colors disabled:opacity-40"
            >
              {busy ? 'Applying…' : 'Confirm'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const ControlCard = ({ label, value, unit, icon: Icon, color, disabled, onChange }) => (
  <div className="p-4 rounded-xl border border-gray-200 bg-gray-50/60">
    <div className="flex items-center gap-3 mb-4">
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${color}1a` }}
      >
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-xl font-semibold text-gray-800">
          {value ?? 0}
          {unit && <span className="text-sm text-gray-500 ml-0.5">{unit}</span>}
        </p>
      </div>
    </div>
    <button
      onClick={onChange}
      disabled={disabled}
      className={`w-full py-2 rounded-lg border text-sm font-medium transition-colors ${
        disabled
          ? 'border-gray-200 text-gray-400 cursor-not-allowed'
          : 'border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-white'
      }`}
    >
      {disabled ? 'Disabled' : 'Change'}
    </button>
  </div>
);

const InfoRow = ({ name, value, unit, icon: Icon, color }) => (
  <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50/60">
    <div
      className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
      style={{ backgroundColor: `${color}1a` }}
    >
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500">{name}</p>
      <p className="text-base font-semibold text-gray-800 truncate">
        {value}
        {unit && <span className="text-sm text-gray-500 ml-0.5">{unit}</span>}
      </p>
    </div>
  </div>
);

// The dialogs, and the rules for when a value may be changed at all, kept in
// one place because two parts of the dashboard reach for them: the System
// Controls box down the page, and the cards at the top, which the phone also
// makes tappable. Whoever calls this owns the state, so both sets of controls
// open the same dialog rather than each keeping its own.
export const useDryerControls = ({
  mode,
  moistureSetpoint,
  rateSetpoint,
  autoLocked,
  autoLockedReason,
  readOnly,
  onModeChange,
  onSetpointChange,
}) => {
  const [dialog, setDialog] = useState(null);
  // A mode change that still needs its setpoint before it is sent. Switching
  // to Automatic asks for the moisture setpoint and Manual asks for the rate,
  // the same order the phone uses.
  const [pendingMode, setPendingMode] = useState(null);

  // Same rules as the phone: the moisture setpoint only matters in Automatic,
  // the rate setpoint only in Manual, and neither applies in Local. Vendors
  // read the dashboard without changing anything, so read-only stops all three.
  const moistureDisabled = readOnly || mode === MANUAL || mode === LOCAL;
  const rateDisabled = readOnly || mode === AUTOMATIC || mode === LOCAL;
  const modeDisabled = readOnly;

  const closeDialogs = () => {
    setDialog(null);
    setPendingMode(null);
  };

  const applyMode = async (target) => {
    if (target === AUTOMATIC) {
      setPendingMode('automatic_mode');
      setDialog('moisture');
      return;
    }
    if (target === MANUAL) {
      setPendingMode('manual_mode');
      setDialog('rate');
      return;
    }
    await onModeChange('local_mode');
    closeDialogs();
  };

  // Returns the submit handler for one field, so it must not be async itself —
  // an async outer would hand the dialog a promise where it expects a function.
  const applySetpoint = (path) => async (value) => {
    // The mode goes first when one is riding along, so the dryer is already in
    // the mode the value belongs to before the value lands.
    if (pendingMode) await onModeChange(pendingMode);
    await onSetpointChange(path, value);
    closeDialogs();
  };

  const dialogs = (
    <AnimatePresence>
      <ModeDialog
        key="mode"
        open={dialog === 'mode'}
        currentMode={mode}
        autoLocked={autoLocked}
        autoLockedReason={autoLockedReason}
        onCancel={closeDialogs}
        onConfirm={applyMode}
      />
      <SetpointDialog
        key="moisture"
        open={dialog === 'moisture'}
        title="Moisture Setpoint"
        subtitle={pendingMode === 'automatic_mode' ? 'Switching to Automatic' : null}
        current={moistureSetpoint}
        unit="%"
        color={MOISTURE_COLOR}
        min={0}
        max={100}
        onCancel={closeDialogs}
        onSubmit={applySetpoint('moisture_setpoint')}
      />
      <SetpointDialog
        key="rate"
        open={dialog === 'rate'}
        title="Discharge Rate Setpoint"
        subtitle={pendingMode === 'manual_mode' ? 'Switching to Manual' : null}
        current={rateSetpoint}
        color={RATE_COLOR}
        min={0}
        max={100000}
        onCancel={closeDialogs}
        onSubmit={applySetpoint('discharge_rate_setpoint')}
      />
    </AnimatePresence>
  );

  return {
    moistureDisabled,
    rateDisabled,
    modeDisabled,
    // The guards repeat what the disabled buttons already enforce, so a card
    // that forgets to grey itself out still cannot open a dialog it should not.
    openMoisture: () => !moistureDisabled && setDialog('moisture'),
    openRate: () => !rateDisabled && setDialog('rate'),
    openMode: () => !modeDisabled && setDialog('mode'),
    dialogs,
  };
};

const SystemControls = ({
  mode,
  moistureSetpoint,
  rateSetpoint,
  dryerStateLabel,
  dryerStateColor,
  productName,
  productIconName,
  activeBin,
  predictedMoisture,
  suggestedRate,
  modelState,
  controls,
}) => {
  const product = getProductIcon(productIconName);

  // Named the way the panel's header names it: the operator's label if they
  // gave one, otherwise the bin number. No bin selected is a normal state.
  const binName = activeBin ? activeBin.name || `Bin ${activeBin.binId}` : '—';
  const binPercent =
    activeBin && Number.isFinite(Number(activeBin.percentFull))
      ? `· ${Math.min(Number(activeBin.percentFull), 100).toFixed(0)}% Full`
      : '';

  const modelLabel =
    modelState === 'ready' ? 'Ready'
    : modelState === 'learning' ? 'Learning'
    : modelState === 'waiting_to_initialize' ? 'Initializing'
    : '—';

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border border-gray-200 shadow-sm p-6"
      >
        <h3 className="text-lg font-medium text-gray-800 mb-5">System Controls</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ControlCard
            label="Moisture Setpoint"
            value={moistureSetpoint}
            unit="%"
            icon={Droplet}
            color={MOISTURE_COLOR}
            disabled={controls.moistureDisabled}
            onChange={controls.openMoisture}
          />
          <ControlCard
            label="Discharge Rate Setpoint"
            value={rateSetpoint !== null && rateSetpoint !== undefined ? Math.round(rateSetpoint) : 0}
            icon={Gauge}
            color={RATE_COLOR}
            disabled={controls.rateDisabled}
            onChange={controls.openRate}
          />
          <ControlCard
            label="Control Mode"
            value={MODE_NAME[mode] || 'Unknown'}
            icon={SettingsIcon}
            color={MODE_COLOR[mode] || '#757575'}
            disabled={controls.modeDisabled}
            onChange={controls.openMode}
          />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border border-gray-200 shadow-sm p-6"
      >
        <h3 className="text-lg font-medium text-gray-800 mb-5">System Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InfoRow name="Dryer State" value={dryerStateLabel} icon={DryerIcon} color={dryerStateColor} />
          <InfoRow name="Product" value={productName} icon={product.Icon} color={product.color} />
          <InfoRow name="Predicted Moisture" value={predictedMoisture ?? 0} unit="%" icon={Droplet} color="#0961ad" />
          {/* Zero from the controller means no suggestion (model not initialized or dryer not running) */}
          <InfoRow name="Suggested Rate" value={suggestedRate > 0 ? suggestedRate : '—'} icon={Gauge} color="#f0ad4e" />
          <InfoRow
            name={modelState === 'ready' ? 'Ready for Automatic' : 'Automatic Mode'}
            value={modelLabel}
            icon={modelState === 'ready' ? CheckCircleIcon : Hourglass}
            color={modelState === 'ready' ? '#4caf50' : modelState === 'learning' ? '#ff9800' : '#9e9e9e'}
          />
          <InfoRow name="Active Bin" value={binName} unit={binPercent} icon={GrainBinIcon} color="#466ec3" />
        </div>
      </motion.div>
    </div>
  );
};

export default SystemControls;
